// src/components/auth/PartnerSignup.jsx - REBUILT with Component Library! 🚀
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  KeyRound, 
  User, 
  Mail, 
  Shield,
  ArrowRight, 
  ArrowLeft,
  CheckCircle2,
  Loader2,
  Sparkles,
  UserCheck
} from 'lucide-react';

// Import our complete component library
import {
  AuthLayout,
  AuthCard,
  AuthHeader,
  Button,
  FormField,
  PartnerCodeInput,
  Input,
  PasswordInput,
  BusinessBadge,
  SuccessScreen
} from '../ui';
import { Alert, AlertDescription } from '../ui/alert';

// Import the real auth context
import { useAuth } from '../../contexts/AuthContext.jsx';

const PartnerSignup = () => {
  // Step management
  const [step, setStep] = useState(1); // 1: Code Entry, 2: Account Setup, 3: Success
  const [isLoading, setIsLoading] = useState(false);
  const [validationError, setValidationError] = useState('');
  
  // Auth context
  const { error, verifyPartnerCode, completePartnerSetup, clearError } = useAuth();
  
  // Form data
  const [partnerCode, setPartnerCode] = useState('');
  const [businessInfo, setBusinessInfo] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  
  const navigate = useNavigate();

  // Step 1: Verify Partner Code
  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    clearError();

    try {
      const businessData = await verifyPartnerCode(partnerCode);
      if (businessData) {
        setBusinessInfo(businessData);
        setStep(2);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Create Admin Account
  const handleAccountSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous validation errors
    setValidationError('');
    
    // Validation
    if (formData.password !== formData.confirmPassword) {
      setValidationError('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 8) {
      setValidationError('Password must be at least 8 characters long');
      return;
    }

    setIsLoading(true);
    clearError();

    try {
      const success = await completePartnerSetup(
        partnerCode,
        formData.name,
        formData.email,
        formData.password
      );

      if (success) {
        setStep(3);
        // Auto redirect to dashboard after 3 seconds
        setTimeout(() => {
          navigate('/dashboard');
        }, 3000);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  return (
    <AuthLayout>
      <AuthCard>
        {/* Step 1: Partner Code Entry */}
        {step === 1 && (
          <>
            {/* Header */}
            <AuthHeader
              icon={KeyRound}
              title="Join as Partner"
              subtitle="Enter your partner code to get started"
              iconGradient="from-emerald-500 to-blue-600"
              iconShadow="shadow-emerald-500/25"
            />

            {/* Error Display */}
            {error && (
              <Alert variant="destructive" className="mb-6">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Code Entry Form */}
            <form onSubmit={handleCodeSubmit} className="space-y-6">
              <FormField 
                label="Partner Code" 
                required
                helpText="Format: XXX-XXXXX (provided by your platform administrator)"
              >
                <PartnerCodeInput
                  id="partnerCode"
                  value={partnerCode}
                  onChange={(e) => setPartnerCode(e.target.value)}
                  placeholder="ABC-12345"
                  required
                  disabled={isLoading}
                />
              </FormField>

              <Button
                type="submit"
                variant="success"
                size="md"
                isLoading={isLoading}
                disabled={isLoading || partnerCode.length < 9}
                icon={isLoading ? Loader2 : ArrowRight}
                iconPosition="right"
                className="w-full"
              >
                {isLoading ? 'Verifying...' : 'Verify Code'}
              </Button>
            </form>
          </>
        )}

        {/* Step 2: Account Setup */}
        {step === 2 && (
          <>
            {/* Header */}
            <AuthHeader
              icon={UserCheck}
              title="Create Your Account"
              subtitle=""
              iconGradient="from-blue-500 to-purple-600"
              iconShadow="shadow-blue-500/25"
            />

            {/* Business Badge */}
            {businessInfo && (
              <div className="flex justify-center mb-6">
                <BusinessBadge 
                  businessName={businessInfo.name}
                  variant="emerald"
                  size="md"
                />
              </div>
            )}

            {/* Error Display */}
            {(error || validationError) && (
              <Alert variant="destructive" className="mb-6">
                <AlertDescription>{validationError || error}</AlertDescription>
              </Alert>
            )}

            {/* Account Setup Form */}
            <form onSubmit={handleAccountSubmit} className="space-y-6">
              {/* Name Field */}
              <FormField label="Full Name" required>
                <Input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={handleInputChange('name')}
                  placeholder="Enter your full name"
                  leftIcon={User}
                  required
                  disabled={isLoading}
                />
              </FormField>

              {/* Email Field */}
              <FormField label="Email Address" required>
                <Input
                  type="email"
                  id="email"
                  value={formData.email}
                  onChange={handleInputChange('email')}
                  placeholder="Enter your email"
                  leftIcon={Mail}
                  required
                  disabled={isLoading}
                />
              </FormField>

              {/* Password Field */}
              <FormField label="Password" required>
                <PasswordInput
                  id="password"
                  value={formData.password}
                  onChange={handleInputChange('password')}
                  placeholder="Create a password"
                  required
                  disabled={isLoading}
                />
              </FormField>

              {/* Confirm Password Field */}
              <FormField label="Confirm Password" required>
                <PasswordInput
                  id="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange('confirmPassword')}
                  placeholder="Confirm your password"
                  leftIcon={Shield}
                  required
                  disabled={isLoading}
                />
              </FormField>

              {/* Form Actions */}
              <div className="flex gap-3">
                <Button
                  type="button"
                  onClick={() => setStep(1)}
                  variant="secondary"
                  size="md"
                  icon={ArrowLeft}
                  disabled={isLoading}
                >
                  Back
                </Button>

                <Button
                  type="submit"
                  variant="primary"
                  size="md"
                  isLoading={isLoading}
                  disabled={isLoading || !formData.name || !formData.email || !formData.password || !formData.confirmPassword}
                  icon={isLoading ? Loader2 : ArrowRight}
                  iconPosition="right"
                  className="flex-1"
                >
                  {isLoading ? 'Creating Account...' : 'Create Account'}
                </Button>
              </div>
            </form>
          </>
        )}

        {/* Step 3: Success */}
        {step === 3 && (
          <SuccessScreen
            icon={CheckCircle2}
            title="Welcome Aboard! 🎉"
            message="Your partner account has been successfully created. You'll be redirected to your dashboard in a few seconds."
            footerText="Redirecting to dashboard..."
            footerIcon={Sparkles}
            iconGradient="from-emerald-500 to-green-600"
            iconShadow="shadow-emerald-500/25"
          />
        )}

        {/* Bottom Navigation - Only show on steps 1 & 2 */}
        {step !== 3 && (
          <>
            <div className="mt-8 pt-8 border-t border-slate-700/50">
              <div className="text-center text-sm">
                <span className="text-slate-400">Already have an account?</span>
              </div>
            </div>

            <Button
              as={Link}
              to="/login"
              variant="secondary"
              size="md"
              icon={ArrowLeft}
              className="w-full mt-4"
              disabled={isLoading}
            >
              Back to Login
            </Button>
          </>
        )}
      </AuthCard>
    </AuthLayout>
  );
};

export default PartnerSignup;